import AdyenCheckout from '@adyen/adyen-web/dist/es.modern';
import type Checkout from '@adyen/adyen-web/dist/types/core';
import { useCartStore } from '@checkout/stores';
import { useCheckoutStore } from '@checkout/stores/checkout.store';
import { computed, readonly, ref, useContext } from '@nuxtjs/composition-api';
import { Logger } from '@vue-storefront/core';
import { storeToRefs } from 'pinia';

import { useConfig } from '~/composables';
import { useIPData } from '~/diptyqueTheme/composable/useIPData';

import { default as translations } from './translations';
import type {
  ComponentsConfiguration,
  ComponentsConfigurationCallbacks,
  DeleteStoredMethodPayload,
  DeleteStoredMethodResponse,
  UseAdyen
} from './types';

export const LOGGER_PREFIX = '[Adyen]';

export const useAdyen = (): UseAdyen => {
  const {
    app: {
      $vsf: { $magento, $adyen }
    }
  } = useContext();
  const { config } = useConfig();
  const { userCountry } = useIPData();

  const paymentMethods = ref(null);
  const orderDetails = ref({});
  const loading = ref(false);
  const deleting = ref(false);
  const error = ref({
    getCart: null,
    getPaymentMethods: null,
    submitPayment: null,
    submitAdditionalDetails: null,
    deleteStoredMethod: null
  });

  const cartStore = useCartStore();
  const cart = computed(() => cartStore.cart);

  const getError = (err) => err.response || err.data || err;

  const setOrderDetails = (details) => (orderDetails.value = details);

  const countryCode = computed<string>(() => config.value?.general_country_default || userCountry.value || 'NL');

  const getPaymentMethods = async () => {
    try {
      loading.value = true;
      const cartId = $magento.config.state.getCartId();

      if (!config.value.is_adyen_payments_enabled) {
        return;
      }

      if (!cartId) {
        Logger.error(`[getPaymentMethods] There are no cartId.`);
        return;
      }

      const response = await $adyen.api.getPaymentMethodsRequest({
        cartId: cartId,
        customerToken: $magento.config.state.getCustomerToken()
      });

      if (!response) {
        Logger.error(`[getPaymentMethods] Response is empty.`);
        return;
      }

      setOrderDetails(response);
      paymentMethods.value = response;
      const availablePaymentMethods = $adyen.config?.availablePaymentMethods || [];
      const filteredPaymentMethodsResponse = filterPayments(response.paymentMethodsResponse, availablePaymentMethods);
      return {
        paymentMethodsResponse: filteredPaymentMethodsResponse,
        paymentMethodsExtraDetails: response.paymentMethodsExtraDetails
      };
    } catch (err) {
      error.value.getPaymentMethods = getError(err);
      Logger.error(`${LOGGER_PREFIX} ${err.message}`);
    } finally {
      loading.value = false;
    }
  };

  const deleteStoredMethod = async (payload: DeleteStoredMethodPayload): Promise<DeleteStoredMethodResponse> => {
    const deleteMethodResponse: DeleteStoredMethodResponse = {
      success: false,
      message: 'Something went wrong. Please try again!'
    };

    try {
      deleting.value = true;
      error.value.deleteStoredMethod = null;

      const [success, message] = await $adyen.api.deleteMethodRequest({
        payload: payload,
        customerToken: $magento.config.state.getCustomerToken()
      });

      if (!success) {
        throw message;
      }

      deleteMethodResponse.success = success;
      deleteMethodResponse.message = message;
    } catch (err) {
      error.value.deleteStoredMethod = getError(err);
      Logger.error(`${LOGGER_PREFIX} ${err.message}`);
    } finally {
      deleting.value = false;
    }

    return deleteMethodResponse;
  };

  const getDropinConfig = (
    paymentMethodsResponse,
    paymentMethodsExtraDetails,
    isAuthenticated = false,
    onSubmit = null,
    onChange = null,
    onAdditionalDetails = null,
    onError = null
  ) => {
    const amount = paymentMethodsExtraDetails.scheme.configuration.amount;
    const paymentMethodsConfiguration = {
      card: {
        brands: ['mc', 'visa', 'amex', 'maestro', 'discover', 'jcb', 'cartebancaire', 'bcmc'],
        enableStoreDetails: !!isAuthenticated,
        showStoredPaymentMethods: !!isAuthenticated,
        hasHolderName: true,
        holderNameRequired: true
      }
    };
    if (paymentMethodsExtraDetails?.applepay) {
      paymentMethodsConfiguration['applepay'] = {
        amount: paymentMethodsExtraDetails.applepay.configuration.amount,
        countryCode: countryCode.value,
        buttonType: 'plain',
        buttonColor: 'black'
      };
    }

    // TODO: Fetch paymentMethodsConfiguration from nuxt.config.js
    return {
      amount,
      environment: $adyen.config.environment,
      clientKey: $adyen.config.clientKey,
      country: countryCode.value,
      locale: $magento.config.state.getLocale(),
      translations,
      showPayButton: true,
      paymentMethodsResponse,
      paymentMethodsConfiguration: paymentMethodsConfiguration,
      onSubmit,
      onChange,
      onAdditionalDetails,
      onError
    };
  };

  const getComponentsConfiguration = async (
    onSubmit = null,
    onChange = null,
    onAdditionalDetails = null,
    onError = null,
    paymentMethodsResponse = null,
    paymentMethodsExtraDetails = null
  ): Promise<ComponentsConfiguration> => {
    if (!paymentMethodsResponse) {
      const response = await getPaymentMethods();

      // Handle api/adyen/getPaymentMethodsRequest errors
      if (error.value.getPaymentMethods) {
        onError({
          action: 'getPaymentMethods',
          error: error.value.getPaymentMethods
        });

        return;
      }

      paymentMethodsResponse = response.paymentMethodsResponse;
      paymentMethodsExtraDetails = response?.paymentMethodsExtraDetails;
    }

    return {
      amount: paymentMethodsExtraDetails.scheme.configuration.amount,
      environment: $adyen.config.environment,
      clientKey: $adyen.config.clientKey,
      countryCode: countryCode.value,
      locale: $magento.config.state.getLocale(),
      showPayButton: true,
      buttonType: 'plain',
      buttonColor: 'black',
      paymentMethodsResponse,
      onSubmit,
      onChange,
      onAdditionalDetails,
      onError
    };
  };

  const getComponentsCheckout = async (callbacks: ComponentsConfigurationCallbacks): Promise<Checkout> => {
    const { onError, onAdditionalDetails, paymentMethodsResponse, paymentMethodsExtraDetails } = callbacks;
    const configuration = await getComponentsConfiguration(
      null,
      null,
      onError,
      onAdditionalDetails,
      paymentMethodsResponse,
      paymentMethodsExtraDetails
    );

    const checkout = await AdyenCheckout({
      ...configuration
    });
    return checkout;
  };

  const filterPayments = (response, availableMethods = []) => {
    const methods = Array.isArray(response.paymentMethods) ? response.paymentMethods : Object.values(response.paymentMethods);
    const data = {
      ...response,
      paymentMethods: methods.filter((paymentMethod) => {
        return availableMethods.includes(paymentMethod.type);
      })
    };
    return data;
  };

  const checkoutStore = useCheckoutStore();
  const { isBillingAddressSameAsShipping } = storeToRefs(checkoutStore);

  const submitPayment = async (stateData) => {
    Logger.info('submitPayment', stateData);
    try {
      loading.value = true;
      error.value.submitPayment = null;

      const response = await $adyen.api.makePaymentRequest({
        cartId: $magento.config.state.getCartId(),
        customerToken: $magento.config.state.getCustomerToken(),
        stateData,
        cart: cart.value,
        isBillingAddressSameAsShipping: isBillingAddressSameAsShipping.value,
        userAgent: window.navigator.userAgent || ''
      });
      setOrderDetails(response);
    } catch (err) {
      error.value.submitPayment = getError(err);
      Logger.error(`${LOGGER_PREFIX} ${err.message}`);
    } finally {
      loading.value = false;
    }
  };

  const submitAdditionalDetails = async (stateData) => {
    try {
      loading.value = true;
      error.value.submitAdditionalDetails = null;
      const response = await $adyen.api.submitAdditionalDetailsRequest({
        stateData: stateData,
        customerToken: $magento.config.state.getCustomerToken(),
        cartId: $magento.config.state.getCartId()
      });
      setOrderDetails(response);
    } catch (err) {
      error.value.submitAdditionalDetails = getError(err);
      Logger.error(`${LOGGER_PREFIX} ${err.message}`);
    } finally {
      loading.value = false;
    }
  };

  return {
    getPaymentMethods,
    getDropinConfig,
    submitPayment,
    submitAdditionalDetails,
    setOrderDetails,
    getComponentsConfiguration,
    getComponentsCheckout,
    deleteStoredMethod,
    paymentMethods: computed(() => paymentMethods.value),
    orderDetails: computed(() => orderDetails.value),
    loading: readonly(computed(() => loading.value)),
    deleting: readonly(computed(() => deleting.value)),
    error: readonly(computed(() => error.value))
  };
};
